import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BookOpen } from "lucide-react";
import { t } from "@/lib/i18n";

const LibraryLendCard = ({ existing, onLent }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    library_name: existing?.library_name || "",
    monitor_id: existing?.monitor_id || "",
    borrow_date: existing?.borrow_date || new Date().toISOString().slice(0, 10),
    consent_share_data: existing?.consent_share_data || false,
  });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/library-lend", form);
      onLent?.(data);
      setOpen(false);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  if (existing && !open) {
    return (
      <div className="card-flat" data-testid="library-lend-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-[#2F524A]" />
            <h3 className="font-serif text-xl">{t("Library-loaned monitor")}</h3>
          </div>
          <button onClick={() => setOpen(true)} className="text-xs text-[#2F524A] hover:underline">{t("Update")}</button>
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
          <dt className="text-[#5C6661]">{t("Library")}</dt><dd className="text-[#1C201F]">{existing.library_name}</dd>
          <dt className="text-[#5C6661]">{t("Monitor ID")}</dt><dd className="text-[#1C201F]">{existing.monitor_id}</dd>
          <dt className="text-[#5C6661]">{t("Borrowed")}</dt><dd className="text-[#1C201F]">{existing.borrow_date}</dd>
          <dt className="text-[#5C6661]">{t("Share data with the program")}</dt>
          <dd className="text-[#1C201F]">{existing.consent_share_data ? "Yes" : "No"}</dd>
        </dl>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="card-flat" data-testid="library-lend-cta">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen size={16} className="text-[#2F524A]" />
          <h3 className="font-serif text-xl">{t("Borrowed a monitor from a library?")}</h3>
        </div>
        <p className="text-sm text-[#5C6661]">
          {t("Log the library and monitor ID so your readings are grouped correctly. You can also opt in to share de-identified data with the program.")}
        </p>
        <button onClick={() => setOpen(true)} className="btn-primary mt-4 !py-2 !px-4 text-sm"
          data-testid="library-open-button">
          {t("Log a library monitor")}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-flat space-y-4" data-testid="library-form">
      <div className="flex items-center gap-2">
        <BookOpen size={16} className="text-[#2F524A]" />
        <h3 className="font-serif text-xl">{t("Library monitor details")}</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="eyebrow block mb-2">{t("Library name *")}</label>
          <input required maxLength={120} className="input-field" value={form.library_name}
            onChange={(e) => setForm((f) => ({ ...f, library_name: e.target.value }))}
            placeholder={t("Carnegie Library of Pittsburgh")}
            data-testid="library-name-input" />
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Monitor ID *")}</label>
          <input required maxLength={60} className="input-field" value={form.monitor_id}
            onChange={(e) => setForm((f) => ({ ...f, monitor_id: e.target.value }))}
            placeholder={t("MON-A0421")}
            data-testid="library-monitor-input" />
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Borrow date *")}</label>
          <input required type="date" max={new Date().toISOString().slice(0, 10)}
            className="input-field" value={form.borrow_date}
            onChange={(e) => setForm((f) => ({ ...f, borrow_date: e.target.value }))}
            data-testid="library-borrow-input" />
        </div>
      </div>
      <label className="flex items-center gap-2 p-3 rounded-xl border border-[#E8ECEA] cursor-pointer hover:border-[#2F524A]">
        <input type="checkbox" className="accent-[#2F524A]" checked={form.consent_share_data}
          onChange={(e) => setForm((f) => ({ ...f, consent_share_data: e.target.checked }))}
          data-testid="library-consent" />
        <span className="text-sm">
          {t("Share my de-identified readings with the program for community reporting.")}
        </span>
      </label>
      {error && <div className="text-sm text-[#9B2C2C] bg-[#FCEDED] border border-[#F5C2C2] rounded-lg px-3 py-2">{error}</div>}
      <div className="flex justify-end gap-3">
        <button type="button" onClick={() => setOpen(false)} className="btn-secondary !py-2 !px-4 text-sm">{t("Cancel")}</button>
        <button type="submit" disabled={saving} className="btn-primary !py-2 !px-5 text-sm"
          data-testid="library-save-button">
          {saving ? t("Saving…") : t("Save monitor")}
        </button>
      </div>
    </form>
  );
};

export default LibraryLendCard;
